import React, { useMemo } from 'react';

import { Alert, Collapse, Flex, Modal } from 'antd-v5';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DatePicker, Text } from 'scouting-ui/lib';

import type { MeritBadgeRequirement } from '@appTypes/esb';
import AvatarPill from '@modules/bulkEntry/components/ConfirmationModal/AvatarPill';
import { sortBulkRequirements } from '@modules/bulkEntry/duck/utils/requirementUtils';
import { intl } from '@shared/localization';

import {
  completionDateSel,
  selectedRequirementsSetSel,
  selectedYouthByUserIdSel,
  selectedYouthSel,
} from '../duck/selectors';
import { bulkEntryActions } from '../duck/slice';
import type { YouthSubmitResult } from '../duck/types';
import { groupErrorsByMessage } from '../duck/utils';
import styles from './MBCConfirmationModal.module.scss';

export type { RequirementError, YouthSubmitResult } from '../duck/types';

interface MBCConfirmationModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  requirements: MeritBadgeRequirement[];
  isLoading?: boolean;
  disableSubmit?: boolean;
  submitResults?: YouthSubmitResult[];
}

export function MBCConfirmationModal(props: MBCConfirmationModalProps) {
  const {
    show,
    onClose,
    onSubmit,
    requirements,
    isLoading,
    disableSubmit,
    submitResults = [],
  } = props;

  const dispatch = useDispatch();
  const selectedYouth = useSelector(selectedYouthSel);
  const selectedYouthByUserId = useSelector(selectedYouthByUserIdSel);
  const selectedRequirements = useSelector(selectedRequirementsSetSel);
  const completionDate = useSelector(completionDateSel);

  const requirementById = useMemo(
    () => new Map(requirements.map(req => [Number(req.id), req])),
    [requirements],
  );

  const sortedRequirementNumbers = useMemo(() => {
    if (!selectedRequirements || selectedRequirements.size === 0) return [];

    return (sortBulkRequirements(requirements) as MeritBadgeRequirement[])
      .filter(req => selectedRequirements.has(Number(req.id)))
      .map(req => req.requirementNumber || req.listNumber)
      .filter(Boolean);
  }, [requirements, selectedRequirements]);

  const handleRemoveYouth = (memberId: number) => {
    const youth = selectedYouth.find(y => y.memberId === memberId);
    if (youth) {
      dispatch(bulkEntryActions.toggleYouth({ youth, checked: false }));
    }
  };

  const getYouthName = (userId: number) => {
    const youth = selectedYouthByUserId.get(userId);
    if (youth) {
      return `${youth.firstName} ${youth.lastName}`;
    }
    return `User ${userId}`;
  };

  const hasResults = submitResults.length > 0;
  const hasErrors = submitResults.some(r => r.errors.length > 0);
  const hasSuccesses = submitResults.some(r => r.successCount > 0);

  const submitDisabled =
    disableSubmit || selectedYouth.length === 0 || isLoading;

  return (
    <Modal
      open={show}
      width={800}
      destroyOnClose
      title={
        <FormattedMessage id="quickRankAdvancement.confirmationModal.title" />
      }
      onCancel={onClose}
      footer={
        <Flex gap="middle" justify="space-between" align="center">
          <DatePicker
            onChange={date => {
              dispatch(
                bulkEntryActions.setCompletionDate(
                  date ? date.toJSDate() : undefined,
                ),
              );
            }}
            value={
              completionDate ? DateTime.fromJSDate(completionDate) : undefined
            }
            className={styles.datePicker}
            placeholder={intl.formatMessage({
              id: 'bulkEntry.approveInfo.completionDate',
            })}
            format="MM/dd/yyyy"
            disabledDate={date => date > DateTime.now()}
            allowClear
          />
          <Button
            loading={isLoading}
            onClick={onSubmit}
            disabled={submitDisabled}
          >
            <FormattedMessage id="shared.submit" />
          </Button>
        </Flex>
      }
    >
      <div className={styles.container}>
        <div>
          <Text paragraph font="body-body-4">
            <FormattedMessage id="quickRankAdvancement.confirmationModal.subtitle" />
          </Text>
        </div>

        {hasResults && (
          <div className={styles.resultsContainer}>
            {hasSuccesses && (
              <Alert
                type="success"
                className={styles.resultAlert}
                message={
                  <FormattedMessage id="page.mbcBulkEntry.modal.successTitle" />
                }
                description={
                  <Flex vertical gap={4}>
                    {submitResults
                      .filter(r => r.successCount > 0)
                      .map(result => (
                        <div key={`success-${result.userId}`}>
                          <strong>{getYouthName(result.userId)}:</strong>{' '}
                          <FormattedMessage
                            id="page.mbcBulkEntry.modal.successCount"
                            values={{ count: result.successCount }}
                          />
                        </div>
                      ))}
                  </Flex>
                }
              />
            )}

            {hasErrors && (
              <Alert
                type="error"
                className={styles.resultAlert}
                message={
                  <FormattedMessage id="page.mbcBulkEntry.modal.errorsTitle" />
                }
                description={
                  <Flex vertical gap={8}>
                    {submitResults
                      .filter(r => r.errors.length > 0)
                      .map(result => {
                        const grouped = groupErrorsByMessage(
                          result.errors,
                          requirementById,
                        );

                        const errorItems = grouped.map(
                          ({ message, reqLabels }) => (
                            <div key={message}>
                              Requirements {reqLabels.join(', ')}: {message}
                            </div>
                          ),
                        );

                        const groupedCount = grouped.length;

                        return (
                          <div key={`error-${result.userId}`}>
                            {groupedCount >= 5 ? (
                              <Collapse
                                size="small"
                                items={[
                                  {
                                    key: result.userId,
                                    label: (
                                      <>
                                        <strong>
                                          {getYouthName(result.userId)}
                                        </strong>{' '}
                                        ({groupedCount} errors)
                                      </>
                                    ),
                                    children: errorItems,
                                  },
                                ]}
                              />
                            ) : (
                              <>
                                <strong>{getYouthName(result.userId)}:</strong>
                                {errorItems}
                              </>
                            )}
                          </div>
                        );
                      })}
                  </Flex>
                }
              />
            )}
          </div>
        )}

        <div className={styles.body}>
          <div className={styles.left}>
            <Text>
              <FormattedMessage
                id="quickRankAdvancement.confirmationModal.scouts"
                values={{ count: selectedYouth.length }}
              />
            </Text>

            <div className={styles.pillsContainer}>
              {selectedYouth.map(youth => (
                <AvatarPill
                  key={youth.memberId}
                  person={{
                    userId: youth.memberId,
                    firstName: youth.firstName,
                    lastName: youth.lastName,
                  }}
                  onClick={() => handleRemoveYouth(youth.memberId)}
                />
              ))}
            </div>
          </div>
          <div className={styles.right}>
            <Text>
              <FormattedMessage id="quickRankAdvancement.confirmationModal.requirements" />{' '}
              ({sortedRequirementNumbers.length})
            </Text>
            <p>{sortedRequirementNumbers.join(', ')}</p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
